import { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement, LineElement, BarElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { api } from './api';

ChartJS.register(ArcElement, LineElement, BarElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend, Filler);

// ── Helpers ──────────────────────────────────────────────────────────────────
const MONTH_LETTERS = ['J','F','M','A','M','J','J','A','S','O','N','D'];
const MONTH_SHORT   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const money = n => '$' + Math.round(Number(n) || 0).toLocaleString();
const money1k = n => {
  const v = Math.round(Number(n) || 0);
  return Math.abs(v) >= 1000 ? '$' + (v/1000).toFixed(1) + 'k' : '$' + v;
};
const pct    = n => (Number(n) || 0).toFixed(1) + '%';
const deltaPct = (cur, prior) => {
  if (!prior || prior === 0) return null;
  return Math.round(((cur - prior) / prior) * 100);
};

function formatWeek(weekStart) {
  if (!weekStart) return '';
  const d = new Date(weekStart + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function formatMonth({ yr, mo }) {
  if (!yr || !mo) return '';
  return `${MONTH_SHORT[mo-1]} ${yr}`;
}

// ── Main component ──────────────────────────────────────────────────────────
export default function UpworkDashboard() {
  const [period, setPeriod]   = useState('12mo');
  const [data,   setData]     = useState(null);
  const [count,  setCount]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,  setError]    = useState('');
  const [showImport, setShowImport] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const [dash, cnt] = await Promise.all([
        api.getUpworkDashboard(period),
        api.countUpworkTxn(),
      ]);
      setData(dash); setCount(cnt);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  async function handleClear() {
    const confirmation = window.prompt(
      `This will permanently delete ALL ${count?.total || 0} Upwork transactions from the database. Time entries and projects are not affected.\n\nType DELETE to confirm:`
    );
    if (confirmation !== 'DELETE') return;
    try {
      // Server now requires the current row count as an explicit confirmation
      // in the request body — see /api/upwork-txn/clear.
      const res = await api.clearUpworkTxn(count?.total || 0);
      alert(`Cleared ${res.deleted} transactions.`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  // Empty-state view (no data imported yet)
  if (!loading && count && count.total === 0) {
    return (
      <div>
        <div className="upw-empty">
          <div className="upw-empty-icon">↑</div>
          <h2>No Upwork transactions yet</h2>
          <p>Import your Upwork Transaction report CSV to see your earnings, costs, and client breakdown.</p>
          <button className="btn-primary" onClick={() => setShowImport(true)}>↑ Import Transaction CSV</button>
          <p className="upw-empty-note">
            Get this from Upwork: Reports → Transactions → Export CSV.<br />
            Different from the Time log CSV used by the Time Tracker tab.
          </p>
        </div>
        {showImport && <ImportTxnModal onClose={() => setShowImport(false)} onComplete={load} />}
      </div>
    );
  }

  return (
    <div>
      {/* ── Controls ── */}
      <div className="upw-controls">
        <div className="upw-period">
          <span style={{ color: '#737370' }}>Period</span>
          <select value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="12mo">Last 12 months</option>
            <option value="ytd">Year to date</option>
            <option value="all">All time</option>
          </select>
          {count && <span className="upw-count">{count.total} transactions · {count.earliest} → {count.latest}</span>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn-outline" onClick={() => setShowImport(true)}>↑ Import</button>
          <button className="btn-outline" onClick={() => window.print()}>↓ PDF snapshot</button>
          <button className="btn-outline btn-danger" onClick={handleClear}>⨯ Clear all</button>
        </div>
      </div>

      {error   && <p className="error-msg">{error}</p>}
      {loading && <p style={{ color: '#737370', padding: '2rem 0', textAlign: 'center' }}>Loading dashboard…</p>}

      {!loading && data && (
        <DashboardContent data={data} />
      )}

      {showImport && <ImportTxnModal onClose={() => setShowImport(false)} onComplete={load} />}
    </div>
  );
}

// ── Dashboard main content ───────────────────────────────────────────────────
function DashboardContent({ data }) {
  const { summary, prior_summary, monthly, top_clients, concentration_pct,
          weekly, records, connects_efficiency } = data;

  // Summary cards with YoY deltas
  const grossDelta = deltaPct(summary.gross, prior_summary.gross);
  const netDelta   = deltaPct(summary.net, prior_summary.net);
  const hoursDelta = deltaPct(summary.hours_billed, prior_summary.hours_billed);
  const clientsDelta = summary.active_clients - prior_summary.active_clients;

  // Filter monthly data to the last 12 months for chart
  const last12 = monthly.slice(-12);

  // Build YoY comparison — for each month 1-12, pull this-year and last-year gross
  const now = new Date();
  const thisYr = now.getUTCFullYear();
  const lastYr = thisYr - 1;
  const yoyThis = Array(12).fill(0);
  const yoyLast = Array(12).fill(0);
  monthly.forEach(m => {
    if (m.yr === thisYr)  yoyThis[m.mo - 1] = m.gross;
    if (m.yr === lastYr)  yoyLast[m.mo - 1] = m.gross;
  });
  const yoyMax = Math.max(...yoyThis, ...yoyLast, 1);

  // Active clients per month (sparkline) — last 24 months
  const activeSeries = monthly.slice(-24).map(m => m.active_clients);
  const activeMax    = Math.max(...activeSeries, 1);
  const activePeak   = monthly.reduce((m, row) => row.active_clients > (m?.active_clients || 0) ? row : m, null);

  // Overhead breakdown
  const costs = [
    { label: 'Service fees',    value: summary.service_fees,      color: '#D85A30' },
    { label: 'Connects',        value: summary.connects_cost,     color: '#BA7517' },
    { label: 'Subscription',    value: summary.subscription_cost, color: '#7F77DD' },
    { label: 'Refunds',         value: summary.refunds,           color: '#888780' },
    { label: 'Withdrawal fees', value: summary.withdrawal_fees,   color: '#B4B2A9' },
  ].filter(c => c.value > 0);
  const maxCost = Math.max(...costs.map(c => c.value), 1);

  return (
    <>
      {/* ── Summary cards ── */}
      <div className="upw-card-row upw-card-4">
        <SummaryCard label="Gross earnings" value={money(summary.gross)} delta={grossDelta} />
        <SummaryCard label="Net earnings"   value={money(summary.net)}   delta={netDelta} />
        <SummaryCard label="Hours billed"   value={Math.round(summary.hours_billed).toLocaleString()} delta={hoursDelta} />
        <SummaryCard label="Active clients" value={summary.active_clients}
          deltaRaw={clientsDelta > 0 ? `+${clientsDelta} vs prior 12mo` : clientsDelta < 0 ? `${clientsDelta} vs prior 12mo` : '—'}
          deltaPositive={clientsDelta >= 0}
        />
      </div>

      {/* ── Records strip ── */}
      <div className="upw-card-row upw-card-5">
        <RecordCard label="Biggest week" value={records.biggest_week ? money(records.biggest_week.gross) : '—'}
          sub={records.biggest_week ? formatWeek(records.biggest_week.week_start) : ''} />
        <RecordCard label="Biggest month" value={records.biggest_month ? money(records.biggest_month.gross_earnings) : '—'}
          sub={records.biggest_month ? formatMonth(records.biggest_month) : ''} />
        <RecordCard label="Avg $/hr net" value={'$' + (summary.avg_rate_net || 0).toFixed(0)}
          sub="realized after fees" />
        <RecordCard label="Cash flow delay" value={records.cash_flow_delay ? `${records.cash_flow_delay.toFixed(1)} d` : '—'}
          sub="earn → withdraw" />
        <RecordCard label="Billing streak" value={`${records.billing_streak} wks`}
          sub={`${records.dry_weeks.length} dry weeks`} />
      </div>

      {/* ── Section: Performance ── */}
      <div className="upw-section-head">Performance over time</div>

      <div className="upw-card-row upw-card-2">
        <div className="upw-chart-card">
          <div className="upw-chart-title">Monthly gross vs net <small>last 12 mo</small></div>
          <div className="upw-chart">
            <Bar
              data={{
                labels: last12.map(m => MONTH_LETTERS[m.mo-1]),
                datasets: [
                  { label: 'Gross', data: last12.map(m => m.gross), backgroundColor: '#85B7EB', borderRadius: 3 },
                  { label: 'Net',   data: last12.map(m => m.net),   backgroundColor: '#185FA5', borderRadius: 3 },
                ],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 }, padding: 8 } } },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                  y: { ticks: { font: { size: 10 }, callback: v => money1k(v) } },
                },
              }}
            />
          </div>
        </div>

        <div className="upw-chart-card">
          <div className="upw-chart-title">This year vs last year <small>gross $ by month</small></div>
          <div className="upw-yoy">
            {MONTH_LETTERS.map((L, i) => (
              <div key={i} className="upw-yoy-col">
                <div className="upw-yoy-bars">
                  <div className="upw-yoy-bar a" style={{ height: `${(yoyLast[i] / yoyMax) * 100}%` }} title={`Last yr ${L}: ${money(yoyLast[i])}`} />
                  <div className="upw-yoy-bar b" style={{ height: `${(yoyThis[i] / yoyMax) * 100}%` }} title={`This yr ${L}: ${money(yoyThis[i])}`} />
                </div>
                <span>{L}</span>
              </div>
            ))}
          </div>
          <div className="upw-legend">
            <span><span className="dot" style={{ background: '#B5D4F4' }} />Last year ({lastYr})</span>
            <span><span className="dot" style={{ background: '#185FA5' }} />This year ({thisYr})</span>
          </div>
        </div>
      </div>

      <div className="upw-chart-card" style={{ marginBottom: 14 }}>
        <div className="upw-chart-title">Effective hourly rate over time <small>net $/hr, monthly</small></div>
        <div className="upw-chart">
          <Line
            data={{
              labels: last12.map(m => MONTH_LETTERS[m.mo-1]),
              datasets: [{
                data: last12.map(m => Math.round(m.effective_rate || 0)),
                borderColor: '#1D9E75',
                backgroundColor: 'rgba(29,158,117,0.12)',
                fill: true, tension: 0.3, pointRadius: 3,
              }],
            }}
            options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => '$' + ctx.parsed.y + '/hr' } } },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                y: { ticks: { font: { size: 10 }, callback: v => '$' + v } },
              },
            }}
          />
        </div>
      </div>

      <div className="upw-chart-card" style={{ marginBottom: 14 }}>
        <div className="upw-chart-title">Active clients by month
          <small>{activePeak ? `peak: ${activePeak.active_clients} in ${formatMonth(activePeak)}` : 'count'}</small>
        </div>
        <div className="upw-spark-row">
          {activeSeries.map((v, i) => (
            <div key={i} className="upw-spark-cell" style={{ height: `${(v / activeMax) * 100}%` }} />
          ))}
        </div>
      </div>

      {/* ── Section: Upwork overhead ── */}
      <div className="upw-section-head">Upwork overhead</div>

      <div className="upw-chart-card" style={{ marginBottom: 14 }}>
        <div className="upw-oh-hero">
          <div>
            <div className="upw-oh-big">{pct(summary.overhead_pct)}</div>
            <div className="upw-oh-sub">of gross earnings · {money(summary.total_costs)} total</div>
          </div>
          <div className="upw-oh-breakdown" style={{ minWidth: 0 }}>
            {costs.map(c => {
              const p = summary.gross > 0 ? (c.value / summary.gross) * 100 : 0;
              return (
                <div className="upw-oh-row" key={c.label}>
                  <div className="upw-oh-lbl">{c.label}</div>
                  <div className="upw-oh-bar"><div className="upw-oh-fill" style={{ width: `${(c.value / maxCost) * 100}%`, background: c.color }} /></div>
                  <div className="upw-oh-dol">{money(c.value)}</div>
                  <div className="upw-oh-pct">{p.toFixed(1)}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="upw-card-row upw-card-2">
        <div className="upw-chart-card">
          <div className="upw-chart-title">Overhead % over time <small>monthly</small></div>
          <div className="upw-chart">
            <Line
              data={{
                labels: last12.map(m => MONTH_LETTERS[m.mo-1]),
                datasets: [{
                  data: last12.map(m => Number(m.overhead_pct.toFixed(1))),
                  borderColor: '#D85A30',
                  backgroundColor: 'rgba(216,90,48,0.12)',
                  fill: true, tension: 0.3, pointRadius: 3,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.parsed.y + '%' } } },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                  y: { ticks: { font: { size: 10 }, callback: v => v + '%' } },
                },
              }}
            />
          </div>
        </div>

        <div className="upw-chart-card">
          <div className="upw-chart-title">Connects efficiency <small>$ per new client won</small></div>
          <div className="upw-chart">
            <Bar
              data={{
                labels: connects_efficiency.map(q => q.quarter),
                datasets: [{
                  data: connects_efficiency.map(q => q.cost_per_new != null ? Math.round(q.cost_per_new) : 0),
                  backgroundColor: '#BA7517', borderRadius: 3,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: {
                  label: ctx => {
                    const q = connects_efficiency[ctx.dataIndex];
                    return q.new_clients
                      ? `$${Math.round(q.cost_per_new)} / client · ${q.new_clients} new`
                      : 'no new clients';
                  }
                } } },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 45, minRotation: 45 } },
                  y: { ticks: { font: { size: 10 }, callback: v => '$' + v } },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Section: Clients ── */}
      <div className="upw-section-head">Clients</div>

      <div className="upw-card-row upw-card-2">
        <div className="upw-chart-card">
          <div className="upw-chart-title">Top clients <small>by net earnings</small></div>
          {top_clients.slice(0, 6).map(c => {
            const max = top_clients[0]?.net || 1;
            return (
              <div className="upw-bar-h" key={c.client_team}>
                <div className="upw-bar-h-label">{c.client_team}</div>
                <div className="upw-bar-h-track"><div className="upw-bar-h-fill" style={{ width: `${(c.net / max) * 100}%` }} /></div>
                <div className="upw-bar-h-val">{money(c.net)}</div>
              </div>
            );
          })}
          {top_clients.length > 6 && (
            <div className="upw-bar-h" style={{ color: '#a8a8a3' }}>
              <div className="upw-bar-h-label">+ {top_clients.length - 6} more</div>
              <div className="upw-bar-h-track">
                <div className="upw-bar-h-fill" style={{
                  width: `${(top_clients.slice(6).reduce((s,c) => s + c.net, 0) / (top_clients[0]?.net || 1)) * 100}%`,
                  background: '#85B7EB',
                }} />
              </div>
              <div className="upw-bar-h-val">{money(top_clients.slice(6).reduce((s,c) => s + c.net, 0))}</div>
            </div>
          )}
        </div>

        <div className="upw-chart-card">
          <div className="upw-chart-title">Client concentration <small>top 3 share</small></div>
          <div style={{ textAlign: 'center', margin: '10px 0' }}>
            <div className="upw-conc-big">{pct(concentration_pct)}</div>
            <div style={{ fontSize: 11, color: '#a8a8a3', marginTop: 2 }}>of net earnings</div>
          </div>
          <div className="upw-conc-stack">
            {top_clients.slice(0, 3).map((c, i) => {
              const total = top_clients.reduce((s,c) => s + c.net, 0) || 1;
              const colors = ['#D4537E', '#ED93B1', '#F4C0D1'];
              return (
                <div key={i} style={{ width: `${(c.net / total) * 100}%`, background: colors[i] }}
                     title={`${c.client_team}: ${pct((c.net / total) * 100)}`} />
              );
            })}
            <div style={{
              width: `${100 - (top_clients.slice(0, 3).reduce((s,c) => s + c.net, 0) / (top_clients.reduce((s,c) => s + c.net, 0) || 1)) * 100}%`,
              background: '#F1EFE8',
            }} />
          </div>
          <div className="upw-conc-desc">
            Your top 3 clients produce <b>{pct(concentration_pct)}</b> of net earnings.
            {concentration_pct > 75
              ? ' High concentration — consider diversifying to reduce risk if one leaves.'
              : concentration_pct > 50
              ? ' Healthy balance. Keep an eye on it if it climbs over 75%.'
              : ' Well diversified client base.'}
          </div>
        </div>
      </div>

      <div className="upw-chart-card" style={{ marginBottom: 14 }}>
        <div className="upw-chart-title">Per-client detail <small>{data.period.label} · sorted by net</small></div>
        <div className="table-wrap" style={{ border: 'none', maxHeight: 300, overflowY: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Gross $</th>
                <th style={{ textAlign: 'right' }}>Fees</th>
                <th style={{ textAlign: 'right' }}>Net $</th>
                <th style={{ textAlign: 'right' }}>$/hr net</th>
                <th style={{ textAlign: 'right' }}>Weeks</th>
                <th style={{ textAlign: 'right' }}>First seen</th>
              </tr>
            </thead>
            <tbody>
              {top_clients.map(c => (
                <tr key={c.client_team}>
                  <td>{c.client_team}</td>
                  <td style={{ textAlign: 'right' }}>{c.hours.toFixed(1)}</td>
                  <td style={{ textAlign: 'right' }}>{money(c.gross)}</td>
                  <td style={{ textAlign: 'right', color: '#737370' }}>{money(c.fees)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>{money(c.net)}</td>
                  <td style={{ textAlign: 'right' }}>{c.avg_rate_net > 0 ? '$' + Math.round(c.avg_rate_net) : '—'}</td>
                  <td style={{ textAlign: 'right', color: '#737370' }}>{c.weeks}</td>
                  <td style={{ textAlign: 'right', color: '#737370', fontSize: 12 }}>{c.first_seen}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Section: Activity patterns ── */}
      <div className="upw-section-head">Activity patterns</div>

      <div className="upw-card-row upw-card-2">
        <div className="upw-chart-card">
          <div className="upw-chart-title">Hours billed per week</div>
          <div className="upw-chart">
            <Bar
              data={{
                labels: weekly.map((_, i) => 'W' + (i + 1)),
                datasets: [{
                  data: weekly.map(w => w.hours),
                  backgroundColor: '#1D9E75', borderRadius: 3,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: {
                  title: ctx => formatWeek(weekly[ctx[0].dataIndex].week_start),
                  label: ctx => ctx.parsed.y.toFixed(1) + ' hours',
                } } },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 12 } },
                  y: { ticks: { font: { size: 10 } } },
                },
              }}
            />
          </div>
        </div>

        <div className="upw-chart-card">
          <div className="upw-chart-title">Connects purchased per week</div>
          <div className="upw-chart">
            <Line
              data={{
                labels: weekly.map((_, i) => 'W' + (i + 1)),
                datasets: [{
                  data: weekly.map(w => w.connects),
                  borderColor: '#BA7517',
                  backgroundColor: 'rgba(186,117,23,0.15)',
                  fill: true, tension: 0.3, pointRadius: 2,
                }],
              }}
              options={{
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: {
                  title: ctx => formatWeek(weekly[ctx[0].dataIndex].week_start),
                  label: ctx => {
                    const w = weekly[ctx.dataIndex];
                    return `${w.connects} connects · ${money(w.connects_cost)}`;
                  },
                } } },
                scales: {
                  x: { grid: { display: false }, ticks: { font: { size: 9 }, maxTicksLimit: 12 } },
                  y: { ticks: { font: { size: 10 } } },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="upw-card-row upw-card-2">
        <div className="upw-chart-card">
          <div className="upw-chart-title">Weeks with no billable hours <small>in period</small></div>
          <div style={{ fontSize: 24, fontWeight: 500, margin: '8px 0' }}>
            {records.dry_weeks.length} wk{records.dry_weeks.length === 1 ? '' : 's'}
            <span style={{ fontSize: 12, fontWeight: 400, color: '#737370', marginLeft: 6 }}>of {weekly.length}</span>
          </div>
          <div className="upw-dry-list">
            {records.dry_weeks.length === 0 && (
              <div style={{ color: '#a8a8a3' }}>None — you billed every week in this period.</div>
            )}
            {records.dry_weeks.slice(0, 6).map((w, i) => (
              <div key={i}><b>{formatWeek(w.week_start)}</b></div>
            ))}
            {records.dry_weeks.length > 6 && (
              <div style={{ color: '#a8a8a3' }}>+ {records.dry_weeks.length - 6} more</div>
            )}
          </div>
        </div>

        <div className="upw-chart-card">
          <div className="upw-chart-title">Seasonal comparison <small>3-yr avg vs this year</small></div>
          <SeasonalTable monthly={monthly} />
          <div style={{ fontSize: 10, color: '#a8a8a3', marginTop: 8, lineHeight: 1.5 }}>
            Normalizes for seasonal dips so a slow December doesn't look like a business problem when it's just Christmas.
          </div>
        </div>
      </div>
    </>
  );
}

// ── Seasonal comparison sub-component ────────────────────────────────────────
function SeasonalTable({ monthly }) {
  const now = new Date();
  const thisYr = now.getUTCFullYear();
  const thisMo = now.getUTCMonth() + 1;

  const byMonth = {};
  monthly.forEach(m => {
    if (!byMonth[m.mo]) byMonth[m.mo] = { thisYr: 0, prior: [] };
    if (m.yr === thisYr) byMonth[m.mo].thisYr = m.gross;
    else if (m.yr >= thisYr - 3 && m.yr < thisYr) byMonth[m.mo].prior.push(m.gross);
  });

  // Show last 4 complete months
  const rows = [];
  for (let i = 0; i < 4; i++) {
    let mo = thisMo - i - 1;
    let label = MONTH_SHORT[(mo + 12 - 1) % 12];
    if (mo < 1) mo += 12;
    const b = byMonth[mo];
    if (!b) continue;
    const avg = b.prior.length ? b.prior.reduce((s, v) => s + v, 0) / b.prior.length : 0;
    const delta = avg > 0 ? Math.round(((b.thisYr - avg) / avg) * 100) : null;
    rows.push({ label, avg, thisYr: b.thisYr, delta });
  }

  return (
    <table style={{ width: '100%', fontSize: 11, marginTop: 6 }}>
      <thead>
        <tr style={{ color: '#737370' }}>
          <td style={{ padding: '3px 6px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Month</td>
          <td style={{ padding: '3px 6px', fontSize: 10, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.04em' }}>3yr avg</td>
          <td style={{ padding: '3px 6px', fontSize: 10, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.04em' }}>This yr</td>
          <td style={{ padding: '3px 6px', fontSize: 10, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Δ</td>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={4} style={{ color: '#a8a8a3', padding: 6 }}>Need 2+ years of history to compare.</td></tr>}
        {rows.map(r => (
          <tr key={r.label}>
            <td style={{ padding: '3px 6px' }}>{r.label}</td>
            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{r.avg ? money(r.avg) : '—'}</td>
            <td style={{ padding: '3px 6px', textAlign: 'right' }}>{r.thisYr ? money(r.thisYr) : '—'}</td>
            <td style={{ padding: '3px 6px', textAlign: 'right',
              color: r.delta == null ? '#a8a8a3' : r.delta >= 0 ? '#3B6D11' : '#A32D2D' }}>
              {r.delta == null ? '—' : `${r.delta >= 0 ? '+' : ''}${r.delta}%`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Small reusable pieces ────────────────────────────────────────────────────
function SummaryCard({ label, value, delta, deltaRaw, deltaPositive }) {
  const isPositive = deltaPositive != null ? deltaPositive : (delta != null && delta >= 0);
  return (
    <div className="upw-metric">
      <div className="upw-metric-label">{label}</div>
      <div className="upw-metric-value">{value}</div>
      {(delta != null || deltaRaw) && (
        <div className={`upw-metric-delta ${isPositive ? 'up' : 'down'}`}>
          {deltaRaw ? deltaRaw : `${delta >= 0 ? '+' : ''}${delta}% vs prior 12mo`}
        </div>
      )}
    </div>
  );
}

function RecordCard({ label, value, sub }) {
  return (
    <div className="upw-metric-sm">
      <div className="upw-metric-label">{label}</div>
      <div className="upw-metric-value-sm">{value}</div>
      {sub && <div className="upw-metric-sub">{sub}</div>}
    </div>
  );
}

// ── Import modal ─────────────────────────────────────────────────────────────
function ImportTxnModal({ onClose, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [result,  setResult]  = useState(null);
  const fileInputRef = useRef(null);

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const text = await file.text();
      const res = await api.importUpworkTxn(text);
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function finish() {
    onComplete && onComplete();
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="modal-title">Import Upwork Transaction CSV</div>
            <div className="modal-sub">Reports → Transactions → Export CSV from Upwork</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>

        {error && <p className="error-msg">{error}</p>}

        {!result && (
          <div style={{ padding: '1.5rem 0', textAlign: 'center' }}>
            <p style={{ color: '#737370', marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
              Upload your Upwork Transaction report. Re-imports are safe — duplicate rows are skipped using the Ref ID.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              style={{ display: 'none' }}
            />
            <button className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={loading}>
              {loading ? 'Importing…' : '↑ Choose CSV file'}
            </button>
          </div>
        )}

        {result && (
          <div style={{ padding: '1rem 0', textAlign: 'center' }}>
            <div style={{ fontSize: 40, color: '#16a34a', marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 14 }}>Import complete</div>
            <div style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.8, maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
              <div>CSV rows parsed: <b>{result.row_count}</b></div>
              <div style={{ color: '#3B6D11' }}>New transactions inserted: <b>{result.inserted}</b></div>
              {result.skipped_duplicates > 0 && (
                <div style={{ color: '#737370' }}>Duplicates skipped: <b>{result.skipped_duplicates}</b></div>
              )}
              {result.skipped_errors > 0 && (
                <div style={{ color: '#A32D2D' }}>Errors: <b>{result.skipped_errors}</b></div>
              )}
              {result.date_range && (
                <div style={{ marginTop: 10, color: '#737370', fontSize: 12 }}>
                  Date range: {result.date_range.min} → {result.date_range.max}
                </div>
              )}
            </div>
            <button className="btn-primary" style={{ marginTop: 20 }} onClick={finish}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
